import { useEffect, useState } from "react";
import Toggle from "../../components/Toggle";
import {
  downloadSettlementReport,
  getSettlementReport,
} from "../../services/transaction";
import toast from "react-hot-toast";
import { getBusinessDetails } from "../../services/settings";
import { useAuth } from "../../context/AuthContextProvider";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";

const SettlementReport = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [businessName, setBusinessName] = useState("");

  const { globalProfile } = useAuth();

  const formatDate = (date) => {
    if (!date) return "";
    if (typeof date === "string") return date;
    if (date instanceof Date && !isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return "";
  };

  const handleBusinessDetails = async () => {
    try {
      const res = await getBusinessDetails();
      setBusinessName(res.data.businessName);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  };

  useEffect(() => {
    handleBusinessDetails();
  }, [globalProfile]);

  const filter = async () => {
    if (!startDate || !endDate) {
      toast.error("Both start and end date must be selected");
      return;
    }
    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);
    if (!formattedStartDate || !formattedEndDate) {
      toast.error("Both start and end date must be valid");
      return;
    }
    setLoading(true);
    try {
      const res = await getSettlementReport(
        formattedStartDate,
        formattedEndDate,
        pageNumber,
        pageSize,
      );
      setReports(res.data?.transactions || []);
      setTotalRecords(res.data?.totalRecords || 0);
      setLoading(false);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setLoading(true);
    const fetchReport = async () => {
      try {
        const res = await getSettlementReport(
          formatDate(startDate),
          formatDate(endDate),
          pageNumber,
          pageSize,
        );
        setReports(res.data?.transactions || []);
        setTotalRecords(res.data?.totalRecords || 0);
        setLoading(false);
      } catch (error) {
        if (error.response) {
          toast.error(error.response.data.message);
          setLoading(false);
        }
      }
    };
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalProfile, pageNumber, pageSize]);

  const handleDownload = async () => {
    try {
      const blobData = await downloadSettlementReport(
        formatDate(startDate),
        formatDate(endDate),
        totalRecords,
      );

      // Get the content type from the response headers
      const contentType = blobData.type;
      // Create a blob from the response data
      const blob = new Blob([blobData], { type: contentType });
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      // Create a temporary <a> element
      const link = document.createElement("a");
      link.href = url;
      // Set the filename
      link.download = `Settlement Report-${businessName}`;
      // Simulate a click on the link to initiate the download
      link.click();
      // Cleanup
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  };

  const tranDateBodyTemplate = (rowData) => {
    if (!rowData.tranDate) return "-";
    return new Date(rowData.tranDate).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const settlementDateBodyTemplate = (rowData) => {
    if (!rowData.settlementDate) return "-";
    return new Date(rowData.settlementDate).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const cardSchemeBodyTemplate = (rowData) => {
    return (
      <div className="text-black-shade text-left font-normal w-[100.97px] h-[30px] rounded-lg py-1 px-1">
        {rowData.cardScheme}
      </div>
    );
  };

  const lcyMscValueBodyTemplate = (rowData) => {
    return rowData.tranAmount && rowData.lcyAmountDueMerchant
      ? Number(rowData.tranAmount) - Number(rowData.lcyAmountDueMerchant)
      : "-";
  };

  return (
    <div className="">
      <div className="flex flex-col xl:flex-row items-start justify-between">
        <h1 className="text-navy-blue text-2xl font-bold">Settlement Report</h1>

        <div className="flex sm:flex-row flex-col justify-center items-center">
          <Toggle />
        </div>
      </div>
      <div className="md:flex items-center justify-between mt-10">
        <div className="md:flex items-center gap-3">
          <div className="mr-4 xl:mr-0 mt-2 xl:mt-0">
            <Calendar
              id="start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.value)}
              placeholder="Start date"
              dateFormat="yy-mm-dd"
              showIcon
              showButtonBar
              maxDate={endDate instanceof Date ? endDate : undefined}
              className="w-full md:w-[170px] h-[40px]"
              inputClassName="h-[40px] px-3 text-sm focus:outline-none"
            />
          </div>

          <div className="mr-4 xl:mr-0 mt-2 xl:mt-0">
            <Calendar
              id="end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.value)}
              placeholder="End date"
              dateFormat="yy-mm-dd"
              showIcon
              showButtonBar
              minDate={startDate instanceof Date ? startDate : undefined}
              className="w-full md:w-[170px] h-[40px]"
              inputClassName="h-[40px] px-3 text-sm focus:outline-none"
            />
          </div>

          <button
            className="text-white bg-primary hover:bg-[rgba(var(--primary-rgb),0.9)] w-[130px] lg:w-[165px] h-[42px] rounded-[10px] mb-3 md:mb-0"
            onClick={filter}
          >
            Filter
          </button>
        </div>
        <div className="relative">
          {!loading && (
            <>
              <button
                onClick={handleDownload}
                className="text-white bg-primary w-[130px] lg:w-[165px] h-[42px] rounded-[10px] mb-3 md:mb-0"
              >
                Export
              </button>
              <span>
                <svg
                  className="absolute left-6 lg:left-10 top-[18px]"
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_4169_8547)">
                    <path
                      d="M6.91254 3.75H6.25004V1.66667C6.25004 1.4375 6.06254 1.25 5.83337 1.25H4.16671C3.93754 1.25 3.75004 1.4375 3.75004 1.66667V3.75H3.08754C2.71671 3.75 2.52921 4.2 2.79171 4.4625L4.70421 6.375C4.86671 6.5375 5.12921 6.5375 5.29171 6.375L7.20421 4.4625C7.46671 4.2 7.28337 3.75 6.91254 3.75ZM2.08337 7.91667C2.08337 8.14583 2.27087 8.33333 2.50004 8.33333H7.50004C7.72921 8.33333 7.91671 8.14583 7.91671 7.91667C7.91671 7.6875 7.72921 7.5 7.50004 7.5H2.50004C2.27087 7.5 2.08337 7.6875 2.08337 7.91667Z"
                      fill="white"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_4169_8547">
                      <rect width="10" height="10" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </span>
            </>
          )}
        </div>
      </div>

      <div className="bg-white mr-5 md:mr-0 sm:mt-4 sm:ml-3 sm:px-7 sm:py-5 rounded-lg shadow-sm">
        <div className="flex items-center sm:justify-between mb-4">
          <div className="flex flex-col-reverse sm:flex-row sm:items-center">
            <p className="text-sm font-bold text-black-shade">
              Report
            </p>
          </div>
        </div>

        <div className="card overflow-x-auto">
          <DataTable
            value={reports}
            loading={loading}
            paginator
            lazy
            first={(pageNumber - 1) * pageSize}
            rows={pageSize}
            totalRecords={totalRecords || 0}
            onPage={(e) => {
              setPageNumber(e.page + 1);
              setPageSize(e.rows);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
            emptyMessage="No report found"
            tableStyle={{ minWidth: "120rem" }}
            stripedRows
            responsiveLayout="scroll"
            className="p-datatable-sm"
          >
            <Column field="transactionId" header="Transaction Id" sortable />
            <Column field="tranAmount" header="Transaction Amount" sortable />
            <Column field="originalAmount" header="Original Amount" sortable />

            <Column
              field="settlementService"
              header="Settlement Service"
              sortable
            />
            <Column
              field="settlementDate"
              header="Settlement Date"
              body={settlementDateBodyTemplate}
              sortable
            />
            <Column field="approvalCode" header="Approval Code" sortable />
            <Column
              field="cardScheme"
              header="Scheme"
              body={cardSchemeBodyTemplate}
              sortable
            />
            <Column field="maskedPan" header="Pan" sortable />
            <Column field="issuerRrn" header="Issuer RRN" sortable />
            <Column
              field="merchantDepositBankName"
              header="Merchant Deposit BankName"
              sortable
            />
            <Column
              field="merchantBankAccount"
              header="Merchant Bank Account"
              sortable
            />
            <Column
              header="Lcy Msc Value"
              body={lcyMscValueBodyTemplate}
            />
            <Column field="totalMscRate" header="Total MscRate" sortable />
            <Column field="textMess" header="Text Message" sortable />
            <Column field="terminalId" header="Terminal ID" sortable />
            <Column
              field="transactionType"
              header="Transaction Type"
              sortable
            />
            <Column
              field="lcyAmountDueMerchant"
              header="Lcy Amount Due Merchant"
              sortable
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};

export default SettlementReport;
